'use client'

import { useChainSensitive } from '@/domain/hooks/useChainSensitive'
import { useStore } from '@/domain/state'
import { useCloseDialog } from '@/domain/hooks/useDialog'
import { ErrorBoundary } from '@/features/errors'
import { Dialog, DialogContent } from '@/ui/atoms/dialog/Dialog'
import { NotConnectedError } from '@/domain/errors/not-connected'
import { NotConnected } from '@/features/errors/NotConnected'
import { UnknownError } from '@/features/errors/UnknownError'
import { useModalTracking } from '@/features/analytics'

function DialogErrorFallback({ error, onClose }: { error: any; onClose: () => void }) {
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent showCloseButton aria-describedby={undefined}>
        {error instanceof NotConnectedError ? <NotConnected /> : <UnknownError error={error} />}
      </DialogContent>
    </Dialog>
  )
}

export function DialogDispatcherContainer() {
  const openedDialog = useStore((state) => state.dialogs.openedDialog)
  const closeDialog = useCloseDialog()
  const closeOnChainChange = openedDialog?.config.options.closeOnChainChange ?? false

  const isChainMatching = useChainSensitive({
    onChainChange: closeOnChainChange ? closeDialog : undefined,
  })

  const dialogOpen = !!openedDialog && (isChainMatching || !closeOnChainChange)
  const modalType =
    openedDialog?.config.element.displayName ?? openedDialog?.config.element.name ?? 'dialog'
  useModalTracking({ open: dialogOpen, modalType })

  if (!openedDialog || (!isChainMatching && closeOnChainChange)) {
    return null
  }

  const {
    config: { element: DialogComponent },
    props,
  } = openedDialog

  return (
    <ErrorBoundary fallback={({ error }) => <DialogErrorFallback error={error} onClose={closeDialog} />}>
      <DialogComponent open setOpen={closeDialog} {...props} />
    </ErrorBoundary>
  )
}
